import React, { useState } from 'react'
import { Download, FileText, Database, Calendar, Users, Award, Activity, Archive, Check } from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { format } from 'date-fns'

interface ExportOption {
  id: string
  name: string
  description: string
  icon: React.ReactNode
  endpoint: string
  filename: string
  available: boolean
}

export const ExportData: React.FC = () => {
  const [exporting, setExporting] = useState<string | null>(null)
  const [dateRange, setDateRange] = useState({
    start: format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd')
  })
  const [includeOptions, setIncludeOptions] = useState({
    includeDeleted: false,
    includeInactive: false,
    includeSystemData: true
  })
  const [exportHistory, setExportHistory] = useState<Array<{
    name: string
    date: string
    size: string
  }>>([])

  const exportOptions: ExportOption[] = [
    {
      id: 'users',
      name: 'Users & Profiles',
      description: 'Export all user accounts, profiles, and settings',
      icon: <Users className="w-5 h-5" />,
      endpoint: '/api/admin/export/users',
      filename: 'users-export',
      available: true
    },
    {
      id: 'chores',
      name: 'Chores & Assignments',
      description: 'Export chores, assignments, and completion history',
      icon: <Activity className="w-5 h-5" />,
      endpoint: '/api/admin/export/chores',
      filename: 'chores-export',
      available: true
    },
    {
      id: 'points',
      name: 'Points & Rewards',
      description: 'Export points transactions, balances, and reward history',
      icon: <Award className="w-5 h-5" />,
      endpoint: '/api/admin/export/points',
      filename: 'points-export',
      available: true
    },
    {
      id: 'calendar',
      name: 'Calendar & Schedule',
      description: 'Export family calendar, custody schedule, and holidays',
      icon: <Calendar className="w-5 h-5" />,
      endpoint: '/api/admin/export/calendar',
      filename: 'calendar-export',
      available: true
    },
    {
      id: 'audit',
      name: 'Audit Logs',
      description: 'Export system audit logs and activity history',
      icon: <FileText className="w-5 h-5" />,
      endpoint: '/api/admin/export/audit-logs',
      filename: 'audit-logs',
      available: true
    },
    {
      id: 'full',
      name: 'Full System Backup',
      description: 'Complete database export including all data',
      icon: <Database className="w-5 h-5" />,
      endpoint: '/api/admin/export/full-backup',
      filename: 'full-backup',
      available: true
    },
    {
      id: 'archive',
      name: 'Archive Old Data',
      description: 'Export and archive data older than 1 year',
      icon: <Archive className="w-5 h-5" />,
      endpoint: '/api/admin/export/archive',
      filename: 'archive-export',
      available: true
    }
  ]

  const handleExport = async (option: ExportOption) => {
    setExporting(option.id)

    try {
      // Build query parameters
      const params = new URLSearchParams({
        start_date: dateRange.start,
        end_date: dateRange.end,
        include_deleted: includeOptions.includeDeleted.toString(),
        include_inactive: includeOptions.includeInactive.toString(),
        include_system: includeOptions.includeSystemData.toString()
      })

      // For full backup and archive, we'll use different logic
      let response
      if (option.id === 'full') {
        response = await apiClient.get(`${option.endpoint}?${params}`, {
          responseType: 'blob'
        })
      } else {
        response = await apiClient.get(`${option.endpoint}?${params}`)
      }

      // Handle the export based on type
      if (option.id === 'full') {
        // For full backup, download as SQL file
        const blob = new Blob([response], { type: 'application/sql' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${option.filename}-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.sql`
        a.click()
        window.URL.revokeObjectURL(url)
      } else {
        // For other exports, convert to CSV
        const csvContent = convertToCSV(response, option.id)
        const blob = new Blob([csvContent], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${option.filename}-${format(new Date(), 'yyyy-MM-dd')}.csv`
        a.click()
        window.URL.revokeObjectURL(url)

        // Add to export history
        const size = (blob.size / 1024).toFixed(2) + ' KB'
        setExportHistory(prev => [
          { name: option.name, date: format(new Date(), 'MMM d, yyyy h:mm a'), size },
          ...prev.slice(0, 9)
        ])
      }

      toast.success(`${option.name} exported successfully`)
    } catch (error) {
      console.error(`Failed to export ${option.name}:`, error)
      toast.error(`Failed to export ${option.name}`)
    } finally {
      setExporting(null)
    }
  }

  const convertToCSV = (data: any, type: string): string => {
    if (!data || (Array.isArray(data) && data.length === 0)) {
      return 'No data available'
    }

    // Handle different data types
    let rows: any[][] = []

    switch (type) {
      case 'users':
        rows = [
          ['ID', 'Username', 'Display Name', 'Email', 'User Type', 'Points', 'Created At', 'Last Active'],
          ...(data.users || []).map((u: any) => [
            u.id,
            u.username,
            u.display_name,
            u.email || '',
            u.user_type,
            u.points || 0,
            u.created_at,
            u.last_active || ''
          ])
        ]
        break

      case 'chores':
        rows = [
          ['ID', 'Chore Name', 'Assigned To', 'Status', 'Points', 'Due Date', 'Completed At', 'Verified By'],
          ...(data.assignments || []).map((a: any) => [
            a.id,
            a.chore_name,
            a.assigned_to,
            a.status,
            a.points,
            a.due_date || '',
            a.completed_at || '',
            a.verified_by || ''
          ])
        ]
        break

      case 'points':
        rows = [
          ['ID', 'User', 'Type', 'Amount', 'Description', 'Date', 'Balance After'],
          ...(data.transactions || []).map((t: any) => [
            t.id,
            t.username,
            t.type,
            t.amount,
            t.description,
            t.created_at,
            t.balance_after || ''
          ])
        ]
        break

      case 'calendar':
        rows = [
          ['Date', 'Event Type', 'Title', 'Kids Present', 'Notes'],
          ...(data.events || []).map((e: any) => [
            e.date,
            e.type,
            e.title || '',
            Array.isArray(e.kids_present) ? e.kids_present.join(', ') : '',
            e.notes || ''
          ])
        ]
        break

      case 'audit':
        rows = [
          ['Timestamp', 'User', 'Action', 'Entity Type', 'Entity', 'Details', 'IP Address'],
          ...(data.logs || []).map((l: any) => [
            l.created_at,
            l.username,
            l.action,
            l.entity_type,
            l.entity_name || '',
            l.details || '',
            l.ip_address || ''
          ])
        ]
        break

      case 'archive':
        rows = [
          ['Archive Date', 'Data Type', 'Record Count', 'Date Range'],
          ...(data.archives || []).map((a: any) => [
            format(new Date(), 'yyyy-MM-dd'),
            a.type,
            a.count,
            `${a.start_date} to ${a.end_date}`
          ])
        ]
        break

      default:
        // Generic CSV conversion
        if (Array.isArray(data)) {
          const headers = Object.keys(data[0] || {})
          rows = [
            headers,
            ...data.map(item => headers.map(h => item[h] || ''))
          ]
        } else {
          rows = [['Data'], [JSON.stringify(data)]]
        }
    }

    // Convert to CSV string
    return rows.map(row =>
      row.map(cell => {
        // Escape quotes and wrap in quotes if contains comma or newline
        const str = String(cell || '')
        if (str.includes(',') || str.includes('\n') || str.includes('"')) {
          return `"${str.replace(/"/g, '""')}"`
        }
        return str
      }).join(',')
    ).join('\n')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Export Data</h2>
        <p className="text-gray-600">Download your data in various formats</p>
      </div>

      {/* Export Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Date Range</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Export Options</h3>
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeOptions.includeDeleted}
                onChange={(e) => setIncludeOptions({ ...includeOptions, includeDeleted: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm">Include deleted records</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeOptions.includeInactive}
                onChange={(e) => setIncludeOptions({ ...includeOptions, includeInactive: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm">Include inactive users</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeOptions.includeSystemData}
                onChange={(e) => setIncludeOptions({ ...includeOptions, includeSystemData: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm">Include system metadata</span>
            </label>
          </div>
        </div>
      </div>

      {/* Export Types */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Available Exports</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {exportOptions.map((option) => (
              <div
                key={option.id}
                className={`border rounded-lg p-4 ${
                  option.available
                    ? 'hover:shadow-md cursor-pointer'
                    : 'opacity-50 cursor-not-allowed'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start">
                    <div className="p-2 bg-blue-100 rounded-lg mr-3">
                      {option.icon}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{option.name}</h4>
                      <p className="text-sm text-gray-500 mt-1">{option.description}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => option.available && handleExport(option)}
                    disabled={!option.available || exporting === option.id}
                    className="ml-4 px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {exporting === option.id ? (
                      <span className="flex items-center">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                        Exporting...
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <Download className="w-3 h-3 mr-1" />
                        Export
                      </span>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Export History */}
      {exportHistory.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-semibold">Recent Exports</h3>
          </div>
          <div className="divide-y">
            {exportHistory.map((item, idx) => (
              <div key={idx} className="px-6 py-3 flex items-center justify-between">
                <div className="flex items-center">
                  <Check className="w-4 h-4 text-green-500 mr-3" />
                  <div>
                    <p className="font-medium text-sm">{item.name}</p>
                    <p className="text-xs text-gray-500">{item.date}</p>
                  </div>
                </div>
                <span className="text-sm text-gray-500">{item.size}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <FileText className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">Export Information</p>
            <ul className="list-disc list-inside space-y-1 text-blue-800">
              <li>Exports are generated in CSV format for easy viewing in Excel or Google Sheets</li>
              <li>Full system backups are exported as SQL files for database restoration</li>
              <li>Large exports may take a few moments to generate</li>
              <li>Exported data respects your current permissions and access levels</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}